import { FaClipboardList, FaRunning, FaMapMarkerAlt, FaChalkboardTeacher, FaUserFriends, FaUserTie } from "react-icons/fa";
import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
} from "recharts";
import KpiCard from "./KpiCard";
import ChartCard from "./ChartCard";
import CustomTooltip from "./CustomTooltip";
import VisitorOverview from "./VisitorOverview";
import "./style.css"; 
import { getBookingData, getDashboardData, getHostelAndCourseData, getVisitorData } from "../../../services/DashboardApiService/DashboardApiService";
import { getAllSportList } from "../../../services/commonApiService/CommonApiService";
import { getBatchAthletes, getCoachesList } from "../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import { getDSOList } from "../../../services/adminApiService/dsoAPIs/dsoApis";

export default function NewDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [visitorLoading, setVisitorLoading] = useState(true);
  const [visitorData, setVisitorData] = useState([]);
  const [filter, setFilter] = useState("week");
  const [chartsExpanded, setChartsExpanded] = useState(false);
  const [venueData, setVenueData] = useState([]);
  const [venueLoading, setVenueLoading] = useState(true);
  const [hostelAndCourse, setHostelAndCourse] = useState([]);
  const [hostelAndCourseLoading, setHostelAndCourseLoading] = useState(true);
  const [totalSports, setTotalSports] = useState(0);
  const [totalCoach, setTotalCoach] = useState(0);
  const [totalAthlete, setTotalAthlete] = useState(0);
  const [totalDso, setTotalDso] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const getData = async () => {
      setDashboardLoading(true);
      setVisitorLoading(true);
      setVenueLoading(true);
      setHostelAndCourse(true);
      setStatsLoading(true);

      try {
        const [
          dashboardRes,
          visitorRes,
          venueRes,
          HostelAndCourseRes,
          sportsRes,
          coachesRes,
          athletesRes,
          dsoRes,
        ] = await Promise.all([
          getDashboardData(),
          getVisitorData(filter),
          getBookingData(),
          getHostelAndCourseData(),
          getAllSportList(),
          getCoachesList({ limit: 1, page: 1 }),
          getBatchAthletes({ per_page: 1 }),
          getDSOList("", "desc", 1, 1),
        ]);

        setDashboardData(dashboardRes);
        setVisitorData(visitorRes?.data?.data || []);
        setVenueData(venueRes?.data);
        setHostelAndCourse(HostelAndCourseRes?.data);
        setTotalSports(sportsRes?.data?.data?.length || 0);
        setTotalCoach(coachesRes?.data?.total || 0);
        setTotalAthlete(athletesRes?.data?.pagination?.total || 0);
        setTotalDso(dsoRes?.data?.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setDashboardLoading(false);
        setVisitorLoading(false);
        setVenueLoading(false);
        setHostelAndCourseLoading(false);
        setStatsLoading(false);
      }
    };

    getData();
  }, [filter]);

  const registration = dashboardData?.data?.data?.registrationData;
  const tooltipMap = {
    week: "Last 7 days",
    month: "This year (monthly data)",
    year: "All time overview",
  };

  return (
    <section className="container">
      {/* HEADER */}
      <div className="mb-4">
        <h4 className="fw-bold">Dashboard</h4>
      </div>

      {/* KPI CARDS */}
      <div className="row g-4 align-items-stretch">
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaClipboardList />}
            title="Total Registration"
            value={registration?.totalRegistration || 0}
            loading={dashboardLoading}
            to="/all-registrations"
          />
        </div>
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaRunning />}
            title="Total Sports"
            value={totalSports}
            loading={statsLoading}
            to="/all-sports"
          />
        </div>
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaMapMarkerAlt />}
            title="Total Venues"
            value={venueData?.data?.totalVenue || 0}
            loading={venueLoading}
            to="/all-venues"
          />
        </div>
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaChalkboardTeacher />}
            title="Total Coach"
            value={totalCoach}
            loading={statsLoading}
            to="/all-coaches"
          />
        </div>
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaUserFriends />}
            title="Total Athlete"
            value={totalAthlete}
            loading={statsLoading}
            to="/all-athletes"
          />
        </div>
        <div className="col-lg-4 col-md-6 d-flex">
          <KpiCard
            icon={<FaUserTie />}
            title="Total DSO"
            value={totalDso}
            loading={statsLoading}
            to="/manage-dso"
          />
        </div>
      </div>

      {/* CHARTS SECTION (collapsible) */}
      <div
        className="d-flex align-items-center gap-2 mt-4"
        style={{ cursor: "pointer" }}
        onClick={() => setChartsExpanded((prev) => !prev)}
      >
        <h5 className="fw-bold mb-0">Charts & Analytics</h5>
        <span
          style={{
            display: "inline-block",
            transition: "transform 0.2s ease",
            transform: chartsExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          &#9660;
        </span>
      </div>

      {chartsExpanded && (
        <>
          <div className="row g-4 mt-2 align-items-stretch">
            {/* LINE */}
            <ChartCard title="Total Registration" badge="Weekly">
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={dashboardData?.data?.data?.weeklyRegistrationData || []}>
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line dataKey="value" stroke="#8757e8" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* GRAPH + SIDE CARD (UNCHANGED) */}
          <div className="row mt-5 g-4">
            {/* GRAPH */}
            <div className="col-lg-8">
              <div className="card-box h-100">
                <div className="d-flex justify-content-between mb-3">
                  <h5 className="fw-bold">Visitor Activity</h5>

                  <div className="custom-tabs">
                    {["week", "month", "year"].map((item) => (
                      <button
                        key={item}
                        className={`tab-btn ${filter === item ? "active" : ""}`}
                        onClick={() => setFilter(item)}
                        data-tooltip={tooltipMap[item] || ""}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ height: 300 }}>
                  {visitorLoading ? (
                    <p>Loading chart...</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={visitorData?.filterBasedData}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" fill="#8757e8" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* SIDE CARD */}
            <div className="col-lg-4 d-flex flex-column gap-4">
              <VisitorOverview data={visitorData} />
              <VisitorOverview
                data={visitorData}
                title="Unique Visitors"
                fields={{
                  total: "totalUniqueVisitorCount",
                  week: "uniqueWeekCount",
                  month: "uniqueMonthCount",
                  year: "uniqueYearCount",
                }}
              />
            </div>
          </div>
        </>
      )}

    </section>
  );
}
